/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { withTransaction } from '@elastic/apm-rum-react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { useRoutes } from 'react-router-dom';
import {
  Box,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Link,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { NavigatingRoutes } from '../../Routes';
import { ReactComponent as ExternalLink } from '../../assets/images/external-link.svg';
import { ReactComponent as NoDataFound } from '../../assets/images/NoDataFound.svg';
import SwalToast from '../../components/Common/SwalToast';
import TransperentSearchBox from '../../components/Common/TransperentSearchBox';
import useHelpSection from '../../Hooks/HelpSection/useHelpSection';
import useCommon from '../../Hooks/useCommon';
import utils from '../../utils/utils';

const NumberOfResultstoShow = 5;
const MoreResultsInDescription = 3;
const handleFocus = (e, theme) => {
  e.currentTarget.style.border = `2px solid ${theme.palette.secondary.main}`;
};
const handleBlur = (e, theme) => {
  e.currentTarget.style.border = `2px solid ${theme?.palette?.grey?.[300]}`;
};

const handleMouseEnter = (e, themeObject) => {
  e.currentTarget.style.backgroundColor = themeObject.palette.other.grey1;
};

const handleMouseLeave = (e, themeObject) => {
  e.currentTarget.style.backgroundColor = themeObject.palette.other.white;
};

const ShowMoreText = ({
  content,
  maxChars,
  boxSx,
  typographySx,
  themeObject,
  showButton = true,
}) => {
  const sanitizedContent = content?.replace(/<[^>]*>/g, '');
  const shouldShowMore = sanitizedContent?.length > maxChars;
  const displayContent = shouldShowMore
    ? sanitizedContent?.slice(0, maxChars)
    : sanitizedContent;

  return (
    <Box sx={boxSx}>
      <Typography
        variant="body2"
        sx={{
          display: 'inline',
          color: themeObject?.palette?.other?.brandBlue,
          ...typographySx,
          '&:focus-visible': {
            outline: 'none',
            border: `2px solid ${themeObject?.palette?.primary?.main} !important`,
          },
        }}
        tabIndex={0}
      >
        {displayContent}
        {shouldShowMore && (
          <>
            {showButton ? (
              <>
                ...
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    color: themeObject?.palette?.primary?.main,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    ml: 0.5,
                    '&:focus-visible': {
                      outline: 'none',
                      border: `2px solid ${themeObject?.palette?.primary?.main} !important`,
                    },
                  }}
                  tabIndex={0}
                  role="button"
                  aria-expanded="false"
                >
                  Show more
                </Typography>
              </>
            ) : (
              '...'
            )}
          </>
        )}
      </Typography>
    </Box>
  );
};
ShowMoreText.defaultProps = {
  maxChars: 100,
  boxSx: {},
  typographySx: {},
  themeObject: {},
  showButton: true,
};
ShowMoreText.propTypes = {
  content: PropTypes.string.isRequired,
  maxChars: PropTypes.number,
  boxSx: PropTypes.oneOfType([PropTypes.object]),
  typographySx: PropTypes.oneOfType([PropTypes.object]),
  themeObject: PropTypes.oneOfType([PropTypes.object]),
  showButton: PropTypes.bool,
};

/**
 * @function SettingsModule
 * @description Renders a module for displaying article title with descriptions.
 * @param {object} props - Props passed to the component.
 * @param {Array} props.content - Array of content items.
 * @param {function} props.setShowDescription - Function to set whether to show the description.
 * @param {boolean} props.showDescription - Flag indicating whether to show the description.
 * @param {number} props.numberOfresults - Number of results to display.
 * @param {object} props.themeObject - Theme object for styling.
 * @returns {JSX.Element} A React component representing the article title with descriptions.
 */
const SettingsModule = ({
  content,
  setShowDescription,
  showDescription,
  numberOfresults,
  themeObject,
  setIsUserSuperadmin,
}) => {
  const [descriptionKey, setDescriptionKey] = useState('');
  const handleStripClick = useCallback(
    (index) => {
      setDescriptionKey(index);
      setShowDescription(true);
    },
    [setDescriptionKey, setShowDescription]
  );

  const handleClickWithIndex = useCallback(
    (index) => () => {
      handleStripClick(index);
    },
    [handleStripClick]
  );

  const handleClickLink = useCallback(() => {
    setIsUserSuperadmin();
  }, []);

  const handleOnEnter = useCallback(
    (e) => handleMouseEnter(e, themeObject),
    [handleMouseEnter]
  );

  const handleOnLeave = useCallback(
    (e) => handleMouseLeave(e, themeObject),
    [handleMouseLeave]
  );

  return (
    <Box>
      {!showDescription &&
        content?.map(
          (item, index) =>
            index < numberOfresults * NumberOfResultstoShow && (
              <Box
                px={0}
                py={0}
                key={item?.id}
                role="button"
                tabIndex={0}
                aria-expanded={showDescription ? 'true' : 'false'}
                aria-controls={`description-${index}`}
                onClick={handleClickWithIndex(index)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleClickWithIndex(index)();
                  }
                }}
                sx={{
                  borderRadius: '8px',
                  marginTop: '23px',
                  width: '424px',
                  height: '75px',
                  cursor: 'pointer',
                  marginLeft: '11px',
                  marginRight: '16px',
                }}
                onMouseEnter={handleOnEnter}
                onMouseLeave={handleOnLeave}
                onFocus={handleOnEnter}
                onBlur={handleOnLeave}
              >
                <Box
                  sx={{
                    marginLeft: '5px',
                    marginTop: '10px',
                    height: '16px',
                  }}
                >
                  <Typography
                    variant="body1"
                    color={themeObject?.palette?.other?.brandBlue}
                    tabIndex={0}
                    sx={(theme) => ({
                      '&:focus-visible': {
                        outline: 'none',
                        border: `2px solid ${theme?.palette?.primary?.main} !important`,
                      },
                    })}
                  >
                    {item?.title}
                  </Typography>
                </Box>
                <ShowMoreText
                  content={content?.[index]?.content}
                  maxChars={98}
                  boxSx={{
                    pt: 1,
                    ml: '5px',
                    width: '397px',
                    height: '34px',
                    opacity: '60%',
                  }}
                  typographySx={{
                    margin: '0px',
                    padding: '0px',
                    pb: 1,
                    flexDirection: 'column',
                    maxHeight: '3em',
                    lineHeight: '16px',
                    textAlign: 'justify',
                  }}
                  themeObject={themeObject}
                />
              </Box>
            )
        )}
      {showDescription && (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignContent="center"
          my={3}
        >
          <Box mx={2}>
            <Typography
              variant="h4"
              tabIndex={0}
              sx={{
                lineHeight: '16px',
                height: '16px',
                '&:focus-visible': {
                  outline: 'none',
                  border: `2px solid ${themeObject?.palette?.primary?.main} !important`,
                },
              }}
            >
              {content?.[descriptionKey]?.title}
            </Typography>
          </Box>
          <ShowMoreText
            content={content?.[descriptionKey]?.content}
            maxChars={500}
            boxSx={{
              ml: 2,
              mt: 2,
              overflow: 'scroll',
              height: 'auto',
            }}
            typographySx={{
              lineHeight: '22px',
              textAlign: 'justify',
              textOverflow: 'ellipsis',
              maxWidth: '416px',
            }}
            themeObject={themeObject}
            showButton={false}
          />
          <Box display="flex" alignItems="center" mt={2}>
            <Typography
              sx={{
                color: themeObject?.palette?.secondary?.contrastText,
                ml: 2,
                maxWidth: '340px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              variant="body1"
              id="learn-more-link"
            >
              Learn more about the {content?.[descriptionKey]?.title}
            </Typography>
            <Link
              aria-labelledby="learn-more-link"
              sx={{
                color: themeObject?.palette?.secondary?.contrastText,
                cursor: 'pointer',
                ml: 1,
                marginTop: '2px',
                '&:focus-visible': {
                  outline: 'none',
                  border: `2px solid ${themeObject?.palette?.primary?.main} !important`,
                },
              }}
              href={`/help?nodeId=${content[descriptionKey]?.id}`}
              target="_blank"
              onClick={handleClickLink}
            >
              <ExternalLink />
            </Link>
          </Box>
          <Divider sx={{ mt: 2.5 }} />
          <Typography
            variant="subtitle1"
            sx={{
              m: 2,
              '&:focus-visible': {
                outline: 'none',
                border: `2px solid ${themeObject?.palette?.primary?.main} !important`,
              },
            }}
            tabIndex={0}
          >
            Related articles
          </Typography>
          {content?.map(
            (item, index) =>
              index !== Number(descriptionKey) &&
              index <= Number(descriptionKey + MoreResultsInDescription) && (
                <Box
                  px={0}
                  py={0}
                  key={item?.id}
                  role="button"
                  tabIndex={0}
                  onClick={handleClickWithIndex(index)}
                  sx={{
                    borderRadius: '8px',
                    pb: 2,
                    width: '424px',
                    height: '75px',
                    cursor: 'pointer',
                    marginLeft: '11px',
                    mt: 2,
                    mr: 2,
                  }}
                  onMouseEnter={handleOnEnter}
                  onMouseLeave={handleOnLeave}
                >
                  <Box
                    aria-label={item?.title}
                    sx={{
                      marginLeft: '5px',
                      height: '16px',
                    }}
                  >
                    <Typography
                      variant="body1"
                      tabIndex={0}
                      sx={(theme) => ({
                        '&:focus-visible': {
                          outline: 'none',
                          border: `2px solid ${theme?.palette?.primary?.main} !important`,
                        },
                      })}
                    >
                      {item?.title}
                    </Typography>
                  </Box>
                  <ShowMoreText
                    content={content?.[index]?.content}
                    maxChars={98}
                    boxSx={{
                      marginTop: '8px',
                      marginLeft: '5px',
                      width: '397px',
                      height: '34px',
                      opacity: '60%',
                    }}
                    typographySx={{
                      m: 0,
                      p: 0,
                      pb: 1,
                      flexDirection: 'column',
                      maxHeight: '3em',
                      lineHeight: '16px',
                    }}
                    themeObject={themeObject}
                  />
                </Box>
              )
          )}
        </Box>
      )}
    </Box>
  );
};

/**
 * @function HelpSectionDrawer
 * @description Renders a side drawer for the help section with search functionality.
 * @param {object} props - Props passed to the component.
 * @param {function} props.setShowHelpSectionDrawer - Function to set whether to show the help section drawer.
 * @param {boolean} props.showHelpSectionDrawer - Flag indicating whether to show the help section drawer.
 * @param {string} props.moduleName - Name of the module.
 * @returns {JSX.Element} A React component representing the help section drawer.
 */
const HelpSectionDrawer = ({
  setShowHelpSectionDrawer,
  showHelpSectionDrawer,
}) => {
  const { common, dispatch, setHelpDrawerKeywordSearch } = useCommon();
  const theme = useTheme();
  const element = useRoutes(NavigatingRoutes);
  const { getHierarchy, setIsUserSuperadmin } = useHelpSection();
  const [content, setContent] = useState([]);
  const [showDescription, setShowDescription] = useState(false);
  const [numberOfresults, setNumberOfresults] = useState(1);
  const [lengthOfContent, setLengthOfContent] = useState(0);
  const [remainingContent, setRemainingContent] = useState(0);
  const [helpSectionTags, setHelpSectionTags] = useState([]);

  useEffect(() => {
    if (element) {
      const currentComponent = NavigatingRoutes.find(
        (route) => route?.name === element?.props?.match?.route?.name
      );
      setHelpSectionTags(currentComponent?.helpSectionTag || []);
    }
  }, [element, setHelpSectionTags]);

  useEffect(() => {
    if (helpSectionTags?.length > 0) {
      getHierarchy({
        id: null,
        tags: helpSectionTags,
        search: null,
      })
        .then((resp) => {
          setContent(resp);
          setLengthOfContent(resp?.length);
        })
        .catch((error) => {
          SwalToast({
            icon: 'error',
            title: error,
          });
        });
    }
  }, [helpSectionTags]);

  const debounce = (callback, delay) => {
    const timeoutRef = useRef(null);
    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);
    const debouncedCallback = (...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    };
    return debouncedCallback;
  };

  /**
   * @function handleSearchContent
   * @description Handles the change event for the search input and updates the Redux store.
   * @param {Event} _event - The event object representing the change event.
   * @returns {void}
   */
  const handleSearchContent = useCallback(
    (_event) => {
      if (_event?.target?.value?.length > 0) {
        dispatch(setHelpDrawerKeywordSearch(_event.target.value));
      } else {
        dispatch(setHelpDrawerKeywordSearch(''));
      }
    },
    [dispatch, setHelpDrawerKeywordSearch]
  );

  /**
   * @function handleSearch
   * @description Debounced version of the search functionality to reduce api calls.
   * @returns {void}
   */
  const handleSearch = debounce(() => {
    if (helpSectionTags?.length > 0) {
      if (common.helpDrawerKeywordSearch?.length) {
        setShowDescription(false);
        getHierarchy({
          id: null,
          tags: null,
          search: common.helpDrawerKeywordSearch,
        })
          .then((resp) => {
            setNumberOfresults(1);
            setContent(resp);
            setLengthOfContent(resp?.length);
          })
          .catch((error) => {
            SwalToast({
              icon: 'error',
              title: error,
            });
          });
      } else {
        getHierarchy({
          id: null,
          tags: helpSectionTags,
          search: null,
        })
          .then((resp) => {
            setContent(resp);
            setLengthOfContent(resp?.length);
          })
          .catch((error) => {
            SwalToast({
              icon: 'error',
              title: error,
            });
          });
      }
    }
  }, 1000);
  useEffect(() => {
    handleSearch();
  }, [common.helpDrawerKeywordSearch]);
  const handleBackToDescription = useCallback(() => {
    setShowDescription(false);
  }, [setShowDescription]);

  const handleShowHelpSectionDrawer = useCallback(() => {
    setShowHelpSectionDrawer(false);
  }, [setShowHelpSectionDrawer]);

  const handleCloseDrawer = useCallback(() => {
    setShowDescription(false);
    setShowHelpSectionDrawer(false);
    setNumberOfresults(1);
  }, [setShowDescription, setShowHelpSectionDrawer, setNumberOfresults]);

  const handleShowMore = useCallback(() => {
    setNumberOfresults((prevState) => prevState + 1);
  }, [numberOfresults, setNumberOfresults]);

  useEffect(() => {
    document.addEventListener('CLOSE_MODALS', handleCloseDrawer);
    return () =>
      document.removeEventListener('CLOSE_MODALS', handleCloseDrawer);
  }, [handleCloseDrawer]);
  useEffect(() => {
    setRemainingContent(
      (Math.floor(lengthOfContent / NumberOfResultstoShow) - numberOfresults) *
        NumberOfResultstoShow
    );
  }, [numberOfresults, lengthOfContent, setRemainingContent]);

  const handleOnFocus = useCallback(
    (e) => handleFocus(e, theme),
    [handleFocus]
  );

  const handleOnBlur = useCallback((e) => handleBlur(e, theme), [handleBlur]);

  return (
    <Drawer
      anchor="right"
      open={showHelpSectionDrawer}
      onClose={handleShowHelpSectionDrawer}
      PaperProps={{
        sx: {
          width: '451px',
          height: '100%',
        },
      }}
      sx={{
        '& .MuiDrawer-paper': {
          boxShadow: 'none',
        },
      }}
      aria-labelledby="help-section-drawer-title"
      aria-modal="false"
      role="dialog"
    >
      <Grid marginTop="70px" overflow="hidden">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginLeft="16px"
        >
          <Box display="flex" alignItems="center" gap="10px">
            {showDescription && (
              <IconButton
                onClick={handleBackToDescription}
                title="Go back"
                aria-label="Go back to description"
              >
                <ArrowBackIcon
                  color="primary"
                  sx={{ width: '20px', height: '20px' }}
                />
              </IconButton>
            )}
            <Typography
              variant="body1"
              component="h2"
              id="help-section-drawer-title"
            >
              Help Section
            </Typography>
          </Box>
          <Box mr="10px">
            <IconButton
              onClick={handleCloseDrawer}
              aria-label="Close"
              title="Close"
            >
              <CloseIcon
                color="primary"
                sx={{
                  width: '20px',
                  height: '20px',
                }}
              />
            </IconButton>
          </Box>
        </Box>
        <Box marginTop="10px">
          <Divider role="presentation" />
        </Box>
        <Box
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          title="Search what you need"
          sx={{
            display: 'flex',
            alignItems: 'center',
            mt: 1,
            ml: 2,
            width: '415px',
            height: '48px',
            border: `2px solid ${theme?.palette?.other?.grey5}`,
            borderRadius: '8px',
            outline: 'none',
            transition: 'border-color 0.3s ease',
          }}
        >
          <TransperentSearchBox
            value={common.helpDrawerKeywordSearch}
            onChange={handleSearchContent}
            aria-label="Search what you need"
            placeholder="Search what you need"
            sx={{ marginTop: '0px' }}
          />
        </Box>
        {!showDescription && common.helpDrawerKeywordSearch?.length > 0 && (
          <Box mb={1} ml={2}>
            <Typography
              variant="body2"
              color={theme?.palette?.other?.brandBlue}
            >
              Showing results from content containing
            </Typography>{' '}
            <Typography
              variant="body1"
              color={theme?.palette?.other?.brandBlue}
            >{`'${common.helpDrawerKeywordSearch}'`}</Typography>
          </Box>
        )}
        {!showDescription &&
          common.helpDrawerKeywordSearch?.length > 0 &&
          lengthOfContent === 0 && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={2}
            >
              <NoDataFound />
              <Typography
                variant="body2"
                tabIndex={0}
                sx={{
                  '&:focus-visible': {
                    outline: 'none',
                    border: `2px solid ${theme?.palette?.primary?.main} !important`,
                  },
                }}
              >
                No matching result found.
              </Typography>
            </Box>
          )}
        <Box sx={{ height: 'calc(100vh - 155px)', overflowY: 'scroll' }}>
          {!utils.isValueEmpty(content) && (
            <SettingsModule
              content={content}
              showDescription={showDescription}
              setShowDescription={setShowDescription}
              numberOfresults={numberOfresults}
              themeObject={theme}
              setIsUserSuperadmin={setIsUserSuperadmin}
            />
          )}
          {!showDescription &&
          numberOfresults * NumberOfResultstoShow < lengthOfContent ? (
            <Box
              display="flex"
              justifyContent="center"
              marginTop="24px"
              marginBottom="32px"
            >
              <Typography
                sx={{
                  color: theme?.palette?.secondary?.contrastText,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  '&:focus-visible': {
                    outline: 'none',
                    border: `2px solid ${theme?.palette?.primary?.main} !important`,
                  },
                }}
                onClick={handleShowMore}
                tabIndex={0}
              >
                Show{' '}
                {remainingContent >= NumberOfResultstoShow
                  ? NumberOfResultstoShow
                  : lengthOfContent -
                    numberOfresults * NumberOfResultstoShow}{' '}
                more articles
              </Typography>
            </Box>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              marginTop="24px"
              marginBottom="32px"
            />
          )}
        </Box>
      </Grid>
    </Drawer>
  );
};

HelpSectionDrawer.propTypes = {
  setShowHelpSectionDrawer: PropTypes.func.isRequired,
  showHelpSectionDrawer: PropTypes.bool.isRequired,
};
export default withTransaction(
  'HelpSectionDrawer',
  'component'
)(HelpSectionDrawer);
